import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { format, parseISO, differenceInCalendarDays } from "date-fns";
import api from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Users, FileText, Scissors, Bell, Clock, CheckCircle, Truck, Package, Calendar, TrendingUp, BarChart3, Inbox, AlertCircle } from "lucide-react";

const statusLabels = {
  pending: "Pending",
  sent_to_worker: "Sent to Worker",
  received_from_worker: "Received from Worker",
  product_ready: "Product Ready",
  received: "Received"
};

const statusIcons = {
  pending: Clock,
  sent_to_worker: Truck,
  received_from_worker: Inbox,
  product_ready: CheckCircle,
  received: Package
};

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [monthlyReports, setMonthlyReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([
      api.get("/dashboard/stats"),
      api.get("/dashboard/monthly-reports")
    ]).then(([statsRes, reportsRes]) => {
      setStats(statsRes.data);
      setMonthlyReports(reportsRes.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="space-y-6">
      {[...Array(4)].map((_, i) => (
        <div key={i} className="h-24 bg-card rounded-sm animate-pulse" />
      ))}
    </div>
  );

  const statCards = [
    { label: "Total Customers", value: stats?.total_customers || 0, icon: Users, color: "#D4AF37" },
    { label: "Total Slips", value: stats?.total_slips || 0, icon: FileText, color: "#722F37" },
    { label: "Active Workers", value: stats?.total_workers || 0, icon: Scissors, color: "#60a5fa" },
    { label: "Notifications", value: stats?.total_notifications || 0, icon: Bell, color: "#4ade80" },
  ];

  const statusCards = [
    { label: "Pending", value: stats?.pending_slips || 0, status: "pending" },
    { label: "Sent to Worker", value: stats?.sent_to_worker || 0, status: "sent_to_worker" },
    { label: "Received from Worker", value: stats?.received_from_worker || 0, status: "received_from_worker" },
    { label: "Product Ready", value: stats?.product_ready || 0, status: "product_ready" },
    { label: "Received", value: stats?.received || 0, status: "received" },
  ];

  return (
    <div data-testid="dashboard-page" className="space-y-6 md:space-y-8">
      {/* Header */}
      <div className="animate-fade-in-up">
        <h1 className="font-serif text-2xl md:text-3xl font-semibold text-foreground">Dashboard</h1>
        <p className="text-muted-foreground text-sm mt-1">Welcome to VR The Darzi Studio</p>
      </div>

      {/* Main Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 stagger-children">
        {statCards.map((stat) => (
          <Card key={stat.label} className="luxury-card rounded-sm cursor-pointer active:scale-[0.98] transition-transform" onClick={() => {
            if (stat.label === "Total Customers") navigate("/customers");
            if (stat.label === "Total Slips") navigate("/slips");
            if (stat.label === "Active Workers") navigate("/workers");
            if (stat.label === "Notifications") navigate("/notifications");
          }}>
            <CardContent className="p-3 md:p-5 flex items-center gap-3 md:gap-4">
              <div className="w-8 h-8 md:w-10 md:h-10 rounded-sm flex items-center justify-center flex-shrink-0" style={{ background: `${stat.color}15` }}>
                <stat.icon className="w-4 h-4 md:w-5 md:h-5" style={{ color: stat.color }} />
              </div>
              <div className="min-w-0">
                <p className="text-xl md:text-2xl font-semibold text-foreground font-serif">{stat.value}</p>
                <p className="text-[10px] md:text-xs text-muted-foreground uppercase tracking-wider truncate">{stat.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Order Status Overview — 6 tiles including Upcoming Deliveries */}
      <div>
        <h2 className="font-serif text-lg font-medium text-foreground mb-4">Order Status Overview</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {statusCards.map((item) => {
            const Icon = statusIcons[item.status];
            return (
              <div
                key={item.status}
                data-testid={`status-${item.status}`}
                className="luxury-card rounded-sm p-4 cursor-pointer"
                onClick={() => navigate(`/slips?status=${item.status}`)}
              >
                <div className="flex items-center gap-2 mb-2">
                  <Icon className="w-4 h-4 text-muted-foreground" />
                  <span className={`text-xs px-2 py-0.5 rounded-sm status-${item.status}`}>{item.label}</span>
                </div>
                <p className="text-2xl font-serif font-semibold text-foreground">{item.value}</p>
              </div>
            );
          })}

          {/* 6th tile — Upcoming Deliveries. Clicking opens the full upcoming list on /slips */}
          <div
            data-testid="upcoming-deliveries-tile"
            className="luxury-card rounded-sm p-4 cursor-pointer"
            onClick={() => navigate("/slips?upcoming=true")}
          >
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="w-4 h-4 text-muted-foreground" />
              <span className="text-xs px-2 py-0.5 rounded-sm bg-[rgba(212,175,55,0.12)] gold-text whitespace-nowrap">Upcoming Deliveries</span>
            </div>
            <p className="text-2xl font-serif font-semibold text-foreground">{stats?.upcoming_deliveries_count || 0}</p>
          </div>
        </div>
      </div>

      {/* Monthly Reports */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-serif text-lg font-medium text-foreground flex items-center gap-2">
            <BarChart3 className="w-5 h-5 gold-text" />
            Monthly Reports
          </h2>
          <button 
            onClick={() => navigate("/analytics")} 
            className="text-xs gold-text hover:underline"
          >
            View Analytics →
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {monthlyReports.map((report, idx) => (
            <Card 
              key={idx} 
              data-testid={`monthly-report-${report.month_num}`}
              className="luxury-card rounded-sm cursor-pointer hover:border-[rgba(212,175,55,0.3)] transition-colors"
              onClick={() => navigate(`/analytics?month=${report.month_num}&year=${report.year}`)}
            >
              <CardContent className="p-3">
                <div className="flex items-center gap-1 mb-2">
                  <Calendar className="w-3 h-3 text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">{report.month} '{report.year.slice(-2)}</span>
                </div>
                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-muted-foreground">Slips</span>
                    <span className="text-sm font-semibold text-foreground">{report.slips_created}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-muted-foreground">Articles</span>
                    <span className="text-sm font-semibold gold-text">{report.total_articles}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-muted-foreground">Done</span>
                    <span className="text-sm font-semibold text-green-400">{report.slips_completed}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Recent Slips */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <h2 className="font-serif text-lg font-medium text-foreground mb-4">Recent Slips</h2>
          <Card className="luxury-card rounded-sm">
            <CardContent className="p-0">
              {stats?.recent_slips?.length > 0 ? (
                <div className="divide-y divide-border">
                  {stats.recent_slips.map((slip) => (
                    <div
                      key={slip.id}
                      className="px-4 py-3 flex items-center justify-between hover:bg-white/[0.02] cursor-pointer transition-colors"
                      onClick={() => navigate(`/slips/${slip.id}`)}
                      data-testid={`recent-slip-${slip.slip_number}`}
                    >
                      <div>
                        <p className="text-sm font-medium text-foreground">{slip.slip_number}</p>
                        <p className="text-xs text-muted-foreground">{slip.customer_name}</p>
                      </div>
                      <Badge variant="outline" className={`text-[10px] rounded-sm status-${slip.status}`}>
                        {statusLabels[slip.status]}
                      </Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="p-6 text-center text-muted-foreground text-sm">No slips yet</p>
              )}
            </CardContent>
          </Card>
        </div>

        <div>
          <h2 className="font-serif text-lg font-medium text-foreground mb-4">Recent Customers</h2>
          <Card className="luxury-card rounded-sm">
            <CardContent className="p-0">
              {stats?.recent_customers?.length > 0 ? (
                <div className="divide-y divide-border">
                  {stats.recent_customers.map((c) => (
                    <div key={c.id} className="px-4 py-3 flex items-center justify-between hover:bg-white/[0.02] transition-colors">
                      <div>
                        <p className="text-sm font-medium text-foreground">{c.name}</p>
                        <p className="text-xs text-muted-foreground">{c.phone}</p>
                      </div>
                      <span className="text-xs gold-text font-medium">{c.customer_id}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="p-6 text-center text-muted-foreground text-sm">No customers yet</p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
